import * as React from 'react';

import { useDebouncedTopbarSearch } from './useDebouncedTopbarSearch';

interface SetSearchParams {
  (params: URLSearchParams, options?: { replace?: boolean }): void;
}

export function useInvoicesListSearchController({
  searchFilter,
  setFilters,
}: {
  searchFilter: string | null | undefined;
  setFilters: (patch: { search: string }) => void;
}) {
  const { searchInput, handleSearchChange } = useDebouncedTopbarSearch({
    value: searchFilter ?? '',
    onCommit: (value) => setFilters({ search: value || '' }),
  });

  return {
    searchInput,
    handleSearchChange,
  };
}

export function useInventoryDetailSearchController({
  searchQuery,
  searchParams,
  setSearchParams,
}: {
  searchQuery: string;
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
}) {
  const handleCommit = React.useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('q', value);
      } else {
        params.delete('q');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { searchInput, handleSearchChange } = useDebouncedTopbarSearch({
    value: searchQuery,
    onCommit: handleCommit,
  });

  return {
    searchInput,
    handleSearchChange,
  };
}

export function useGoposOrdersSearchController({
  orderNumber,
  searchParams,
  setSearchParams,
}: {
  orderNumber: string;
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
}) {
  const handleCommit = React.useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('order_number', value);
      } else {
        params.delete('order_number');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { searchInput, handleSearchChange } = useDebouncedTopbarSearch({
    value: orderNumber,
    onCommit: handleCommit,
  });

  return {
    searchInput,
    handleSearchChange,
  };
}
